import i18n from 'i18next';
import type { ValidationResult as BulkValidationResult, InvalidItemsError } from '../../shared/BulkAdd/types.ts';

const MAX_INPUT_LENGTH = 255;
const MAX_ALLOWED_ITEMS = 2000;

type ValidationResult = {
  error?: string;
};

export const validateTextField = (value: string, selectedItems: Set<string>): ValidationResult => {
  if (value.trim().length > MAX_INPUT_LENGTH) {
    return {
      error: i18n.t('Maximum length is 255 characters.') || 'Maximum length is 255 characters.',
    };
  }
  if (selectedItems.has(value.trim())) {
    return {
      error: i18n.t('Value already exists.') || 'Value already exists.',
    };
  }
  if (value.trim().length !== 0 && selectedItems.size >= MAX_ALLOWED_ITEMS) {
    return {
      error: i18n.t('Maximum number of items reached.') || 'Maximum number of items reached.',
    };
  }
  if (value.trim().length === 0) {
    return {
      error: i18n.t('Value cannot be empty.') || 'Value cannot be empty.',
    };
  }

  return { error: undefined };
};

export const validateBulkTextFields = (values: string[], selectedItems: Set<string>): BulkValidationResult => {
  const errors: InvalidItemsError[] = [];
  const validItems: string[] = [];

  const addError = (error: string, item: string) => {
    const errorObject = errors.find(singleError => singleError.error === error);

    if (errorObject) {
      errorObject.invalidItems.push(item);
    } else {
      errors.push({ error, invalidItems: [item] });
    }
  };

  const addedItems = new Set<string>();

  values.forEach(rawValue => {
    const value = rawValue.trim();

    if (value.length > MAX_INPUT_LENGTH) {
      addError(i18n.t('Maximum length is 255 characters.'), value);
    } else if (selectedItems.has(value)) {
      addError(i18n.t('Value already exists.'), value);
    } else if (addedItems.has(value)) {
      addError(i18n.t('Duplicated value on bulk list.'), value);
    } else if (selectedItems.size + validItems.length >= MAX_ALLOWED_ITEMS) {
      addError(i18n.t('Maximum number of items reached.'), value);
    } else {
      validItems.push(value);
      addedItems.add(value);
    }
  });

  return { errors, validItems };
};
